import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Container,
  Grid,
  Paper,
  MenuItem,
  Stack,
  Link,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import SectionHeading from './SectionHeading';
import {
  SERVICE_OPTIONS,
  URGENCY_OPTIONS,
  WHATSAPP_URL,
  WHATSAPP_DISPLAY,
  CONTACT_EMAIL,
  CALENDLY_URL,
} from '../constants/site';
import { trackEvent } from '../utils/analytics';
import { brandColors } from '../theme';

const Contact = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    serviceInterest: '',
    urgency: 'exploring',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState({ open: false, message: '', severity: '' });

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { name, email, phone, company, serviceInterest, message } = form;
    if (!name || !email || !phone || !company || !serviceInterest || !message) {
      setFeedback({
        open: true,
        message: 'Por favor, completa todos los campos obligatorios.',
        severity: 'error',
      });
      return;
    }
    setLoading(true);
    try {
      await addDoc(collection(db, 'contactMessages'), {
        ...form,
        timestamp: serverTimestamp(),
        attended: false,
      });
      trackEvent('generate_lead', { method: 'contact_form' });
      setFeedback({
        open: true,
        message:
          '¡Gracias! Recibimos tu solicitud. Te contactaremos en menos de 24 horas hábiles.',
        severity: 'success',
      });
      setForm({
        name: '',
        email: '',
        phone: '',
        company: '',
        serviceInterest: '',
        urgency: 'exploring',
        message: '',
      });
    } catch (error) {
      console.error('Error adding document: ', error);
      setFeedback({
        open: true,
        message: 'Hubo un error al enviar tu mensaje. Inténtalo de nuevo o escríbenos por WhatsApp.',
        severity: 'error',
      });
    }
    setLoading(false);
  };

  return (
    <Box
      component="section"
      id="contact"
      sx={{
        py: { xs: 6, md: 8 },
        bgcolor: brandColors.navy,
        color: '#fff',
      }}
    >
      <Container maxWidth="lg">
        <SectionHeading
          dark
          label="Contacto"
          title="Hablemos de tu proyecto"
          subtitle="Cuéntanos qué necesitas. Primera consulta sin costo."
        />
        <Grid container spacing={{ xs: 3, md: 4 }} sx={{ width: '100%' }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Stack spacing={2} sx={{ minWidth: 0 }}>
              {[
                {
                  icon: <AccessTimeIcon />,
                  title: 'Respuesta en 24h',
                  text: 'Revisamos cada solicitud el mismo día hábil.',
                },
                {
                  icon: <WhatsAppIcon />,
                  title: WHATSAPP_DISPLAY,
                  text: (
                    <Link
                      href={WHATSAPP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackEvent('cta_click', { location: 'contact', type: 'whatsapp' })}
                      sx={{ color: brandColors.goldLight }}
                    >
                      Abrir WhatsApp
                    </Link>
                  ),
                },
                {
                  icon: <EmailIcon />,
                  title: CONTACT_EMAIL,
                  text: 'También puedes escribirnos por correo.',
                },
              ].map((item) => (
                <Paper
                  key={item.title}
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    bgcolor: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    minWidth: 0,
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ color: brandColors.gold, mb: 1 }}>{item.icon}</Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#fff',
                      fontWeight: 700,
                      overflowWrap: 'anywhere',
                      wordBreak: 'break-word',
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography variant="body2" component="div" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
                    {item.text}
                  </Typography>
                </Paper>
              ))}
              <Button
                variant="outlined"
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('cta_click', { location: 'contact', type: 'calendly' })}
                fullWidth
                sx={{
                  borderColor: brandColors.gold,
                  color: brandColors.goldLight,
                  '&:hover': { borderColor: brandColors.goldLight, bgcolor: 'rgba(201,169,98,0.1)' },
                }}
              >
                Agendar en Calendly
              </Button>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper
              component="form"
              onSubmit={handleSubmit}
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3, md: 4 },
                bgcolor: '#fff',
                color: 'text.primary',
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              <Grid container spacing={2} sx={{ width: '100%' }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Nombre completo"
                    value={form.name}
                    onChange={handleChange('name')}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Empresa"
                    value={form.company}
                    onChange={handleChange('company')}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={form.email}
                    onChange={handleChange('email')}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Teléfono"
                    value={form.phone}
                    onChange={handleChange('phone')}
                    placeholder="+506 ..."
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label="Servicio de interés"
                    value={form.serviceInterest}
                    onChange={handleChange('serviceInterest')}
                    required
                  >
                    {SERVICE_OPTIONS.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label="¿Cuándo necesitas avanzar?"
                    value={form.urgency}
                    onChange={handleChange('urgency')}
                  >
                    {URGENCY_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Cuéntanos tu situación"
                    multiline
                    rows={4}
                    value={form.message}
                    onChange={handleChange('message')}
                    placeholder="Ej.: Consolidamos inventario en Excel y tardamos 2 días cada semana..."
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={loading}
                    endIcon={loading ? null : <SendIcon />}
                    fullWidth
                    sx={{ py: 1.5 }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Solicitar consulta gratuita'}
                  </Button>
                </Grid>
              </Grid>
              {feedback.open && (
                <Alert
                  severity={feedback.severity}
                  sx={{ mt: 2 }}
                  onClose={() => setFeedback({ ...feedback, open: false })}
                >
                  {feedback.message}
                </Alert>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Contact;
